import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
    NgbNav,
    NgbNavContent,
    NgbNavItem,
    NgbNavItemRole,
    NgbNavLink,
    NgbNavLinkBase,
    NgbNavOutlet,
} from '@ng-bootstrap/ng-bootstrap'

import {
    BaseComponent,
    CrewMemberReassignment,
    FeatureType,
    SavedCrewSchedule,
    SavedForemanSegment,
    SavedWeekendEntry,
    VpJob,
    isoDateStringForApi,
} from '../../shared'
import { ForemanScheduleCrewScheduleTabComponent } from './foreman-schedule-crew-schedule-tab/foreman-schedule-crew-schedule-tab.component'
import { ForemanScheduleCrewsTabComponent } from './foreman-schedule-crews-tab/foreman-schedule-crews-tab.component'
import { ForemanScheduleDashboardTabComponent } from './foreman-schedule-dashboard-tab/foreman-schedule-dashboard-tab.component'
import { ForemanScheduleForemanTabComponent } from './foreman-schedule-foreman-tab/foreman-schedule-foreman-tab.component'
import { ForemanScheduleNotificationsTabComponent } from './foreman-schedule-notifications-tab/foreman-schedule-notifications-tab.component'
import { ForemanScheduleSummaryTabComponent } from './foreman-schedule-summary-tab/foreman-schedule-summary-tab.component'

@Component({
    selector: 'app-foreman-schedule',
    templateUrl: './foreman-schedule.component.html',
    styleUrls: ['./foreman-schedule.component.css'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        NgbNavContent,
        NgbNavOutlet,
        ForemanScheduleSummaryTabComponent,
        ForemanScheduleDashboardTabComponent,
        ForemanScheduleCrewScheduleTabComponent,
        ForemanScheduleCrewsTabComponent,
        ForemanScheduleForemanTabComponent,
        ForemanScheduleNotificationsTabComponent,
    ],
})
export class ForemanScheduleComponent extends BaseComponent {
    private readonly destroyRef = inject(DestroyRef)

    readonly activeJobs = signal<VpJob[]>([])
    readonly crewReassignments = signal<CrewMemberReassignment[]>([])
    readonly savedSegments = signal<SavedForemanSegment[]>([])
    readonly savedWeekendEntries = signal<SavedWeekendEntry[]>([])

    /** Bumped whenever the foreman roster changes, so the schedule tabs reload their context. */
    readonly foremanRevision = signal(0)

    /** Crew Schedule editing and the Crews (temporary reassignment) tab require the Schedule - Edit right. */
    readonly canEdit = computed(() => this.services.authentication.hasFeature([FeatureType.FieldToolsForemanScheduleEdit]))

    /** The Foreman roster/division tab is only available with the Foreman Roster - Edit right. */
    readonly canManageRoster = computed(() =>
        this.services.authentication.hasFeature([FeatureType.FieldToolsForemanRosterEdit]),
    )

    activeTab = 1

    setCrewReassignments(reassignments: CrewMemberReassignment[]) {
        this.crewReassignments.set(reassignments)
    }

    clearCrewReassignments() {
        this.crewReassignments.set([])
    }

    onForemanChanged() {
        this.foremanRevision.update(v => v + 1)
    }

    /** Nudge auto-sizing grids/charts to refit when switching tabs (e.g. the Foreman roster grid). */
    tabChanged() {
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'))
        }, 50)
    }

    constructor() {
        super()
        this.refresh()
    }

    refresh() {
        this.services.data
            .load(true, true, this.services.data.vpJobs.queryAll())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: result => {
                    this.activeJobs.set((result[0] as VpJob[]) ?? [])
                    this.initialLoadingComplete()
                },
                error: () => {
                    this.services.notification.error('Failed to load schedule data')
                    this.initialLoadingComplete()
                },
            })

        this.loadSavedSchedule()
    }

    private loadSavedSchedule() {
        this.services.data
            .get<SavedCrewSchedule>('api/ForemanSchedule/GetSavedCrewSchedule')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: saved => {
                    this.crewReassignments.set(
                        (saved?.reassignments ?? []).map(r => ({
                            id: r.id,
                            memberHrRef: r.memberHrRef,
                            memberName: r.memberName,
                            fromForemanHrRef: r.fromForemanHrRef,
                            fromForemanName: r.fromForemanName,
                            toForemanHrRef: r.toForemanHrRef,
                            toForemanName: r.toForemanName,
                            startDate: isoDateStringForApi(r.startDate as string | Date | null | undefined),
                            endDate: isoDateStringForApi(r.endDate as string | Date | null | undefined),
                        })),
                    )
                    this.savedSegments.set(
                        (saved?.segments ?? []).map(s => ({
                            ...s,
                            startDate: isoDateStringForApi(s.startDate as string | Date | null | undefined),
                        })),
                    )
                    this.savedWeekendEntries.set(
                        (saved?.weekendEntries ?? []).map(w => ({
                            ...w,
                            date: isoDateStringForApi(w.date as string | Date | null | undefined),
                        })),
                    )
                },
                error: () => undefined,
            })
    }
}
